import {phoneDevices, phoneTypes} from '@bpinc/adapters-public-api-types'
import {array, assertDefined, isObject} from '@bpinc/lib-types-helpers'

import {ApiError} from './api-error'


const primitiveTypes = [
    'null', // special case to handle typeof null === 'object'
    'undefined',
    'boolean',
    'number',
    'string',
] as const
type PrimitiveType = typeof primitiveTypes[number]

const complexTypes = [
    'primitiveObject',
    'interactionAssociatedObject',
    'interactionAssociatedObjectsArray',
    'externalKBItems',
    'externalKBSearchResults',
    'externalKBArticle',
    'formattedDate',
    'phoneDeviceData',
    'dispositionTarget',
    'loginData',
    'rescheduleData',
    'arrayOfStrings',
    'dialCandidates',
    'phoneNumbers',
    'recordsValidationResult',
    'contactDetails',
    'activityHistoryInfo',
] as const
type ComplexType = typeof complexTypes[number]

export type Constraint<Values extends readonly unknown[] = readonly unknown[]> = {
    readonly types?: (PrimitiveType | ComplexType)[]
    readonly values?: Values
}

type StructureConstraints = {
    [objectKey: string]: Constraint
}

type ComplexTypeCheckers = Record<ComplexType, (value: unknown) => boolean>

const checkPrimitiveNotNestingObject = (value: unknown): boolean => {
    if (value === null || !isObject(value)) {
        return false
    }
    const values = Object.values(value)
    return !values.some(item => item !== null && !primitiveTypes.includes(typeof item as PrimitiveType))
}

const checkObjectStructure = <T extends object>(value: T, structureConstraint: StructureConstraints): boolean => {
    if (!isObject(value)) {
        return false
    }
    const objKeys = Object.keys(value)
    const cKeys = Object.keys(structureConstraint)

    const allKeysAreOk = objKeys.every(key => {
        if (!cKeys.includes(key)) {
            return false
        }
        return checkTypeConstraint(value[key], structureConstraint[key])
    })
    if (!allKeysAreOk) {
        return false
    }
    const missingKeys = cKeys.filter(key => !objKeys.includes(key))

    // Check that keys missing in object but present in scheme are all optional
    return missingKeys.every(key => {
        return (
            structureConstraint[key].types?.includes('undefined') ||
            structureConstraint[key].values?.includes(undefined)
        )
    })
}

const checkInteractionAssociatedObject = (value: unknown): boolean => {
    if (!isObject(value)) {
        return false
    }
    const constraints: StructureConstraints = {
        id: {types: ['string']},
        type: {types: ['string']},
        displayName: {types: ['string']},
        displayType: {types: ['string']},
        customFields: {types: ['primitiveObject']},
    }
    return checkObjectStructure(value, constraints)
}

const checkInteractionAssociatedObjectsArray = (value: unknown): boolean => {
    if (!(value instanceof Array)) {
        return false
    }
    return value.every(item => checkInteractionAssociatedObject(item))
}

const checkExternalKBArticleItem = (value: unknown): boolean => {
    if (!isObject(value)) {
        return false
    }
    const constraints: StructureConstraints = {
        id: {types: ['string']},
        title: {types: ['string']},
        text: {types: ['string']},
        type: {values: ['article']},
    }
    return checkObjectStructure(value, constraints)
}

const checkExternalKBFolderItem = (value: unknown): boolean => {
    if (!isObject(value)) {
        return false
    }
    const constraints: StructureConstraints = {
        id: {types: ['string']},
        title: {types: ['string']},
        type: {values: ['folder']},
    }
    return checkObjectStructure(value, constraints)
}

const checkExternalKBSearchResults = (value: unknown): boolean => {
    if (!(value instanceof Array)) {
        return false
    }
    return value.every(checkExternalKBArticleItem)
}

const checkExternalKBItems = (value: unknown): boolean => {
    if (!(value instanceof Array)) {
        return false
    }
    return value.every(item => checkExternalKBArticleItem(item) || checkExternalKBFolderItem(item))
}

const checkExternalKBArticle = (value: unknown): boolean => {
    if (!isObject(value)) {
        return false
    }
    const constraints: StructureConstraints = {
        id: {types: ['string']},
        title: {types: ['string']},
        keywords: {types: ['string']},
        answer: {types: ['string']},
        notes: {types: ['string']},
        language: {types: ['string']},
        createdByUser: {types: ['string']},
        lastEditedByUser: {types: ['string']},
        customFields: {types: ['primitiveObject']},
    }
    return checkObjectStructure(value, constraints)
}

const checkDispositionTarget = (value: unknown): boolean => {
    if (!isObject(value)) {
        return false
    }
    const constraints: StructureConstraints = {
        service: {types: ['string', 'undefined']},
        interactionId: {types: ['string', 'undefined']},
    }
    return checkObjectStructure(value, constraints)
}

// format "YYYY-MM-DD HH24:MM:SS"
const formatRegexp = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}/

// does not want to drag along with the Integration API 600 KB of the moment.js
const checkDateFormat = (value: unknown): boolean => {
    if (typeof value !== 'string' || !formatRegexp.test(value)) {
        return false
    }
    const year = parseInt(value.slice(0, 4), 10)
    const month = parseInt(value.slice(5, 7), 10)
    const day = parseInt(value.slice(8, 10), 10)
    const hours = parseInt(value.slice(11, 13), 10)
    const minutes = parseInt(value.slice(14, 16), 10)
    const seconds = parseInt(value.slice(17, 19), 10)

    if (year < 1000 || year > 3000 || month < 1 || month > 12) {
        return false
    }
    const monthLength = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31]
    if (year % 400 === 0 || (year % 100 !== 0 && year % 4 === 0)) {
        monthLength[1] = 29
    }
    if (day < 1 || day > monthLength[month - 1]) {
        return false
    }
    if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59 || seconds < 0 || seconds > 59) {
        return false
    }
    return true
}

const checkPhoneDeviceData = (value: unknown): boolean => {
    if (!isObject(value)) {
        return false
    }
    const constraints: StructureConstraints = {
        type: {values: phoneDevices},
        phone: {types: ['string', 'undefined']},
    }
    return checkObjectStructure(value, constraints)
}

const checkLoginData = (value: unknown): boolean => {
    if (!isObject(value)) {
        return false
    }
    const constraints: StructureConstraints = {
        username: {types: ['string']},
        password: {types: ['string']},
        tenant: {types: ['string', 'undefined']},
    }
    return checkObjectStructure(value, constraints)
}

const checkRescheduleData = (value: unknown): boolean => {
    if (!isObject(value)) {
        return false
    }
    const constraints: StructureConstraints = {
        numberToDial: {types: ['string']},
        fromTime: {types: ['formattedDate']},
        untilTime: {types: ['formattedDate']},
        timezoneCode: {types: ['string', 'undefined']},
    }
    return checkObjectStructure(value, constraints)
}

const checkArrayOfStrings = (value: unknown): boolean => {
    return value instanceof Array && value.every(item => typeof item === 'string')
}

const checkDialCandidates = (value: unknown): boolean => {
    const constraints: StructureConstraints = {
        id: {types: ['string']},
        displayName: {types: ['string']},
        phones: {types: ['phoneNumbers']},
        email: {types: ['string', 'undefined']},
        associatedObjects: {types: ['interactionAssociatedObjectsArray']},
        avatarUrl: {types: ['string', 'undefined']},
    }
    if (!(value instanceof Array)) {
        return false
    }
    return value.every(item => checkObjectStructure(item, constraints))
}

const checkPhoneNumbers = (value: unknown): boolean => {
    const constraints: StructureConstraints = {
        number: {types: ['string']},
        type: {values: phoneTypes},
        displayType: {types: ['string', 'undefined']},
    }
    if (!(value instanceof Array)) {
        return false
    }
    return value.every(item => checkObjectStructure(item, constraints))
}

const checkRecordsValidationResult = (value: unknown): boolean => {
    if (!isObject(value)) {
        return false
    }
    const constraints: StructureConstraints = {
        valid: {types: ['boolean']},
        message: {types: ['string', 'undefined']},
    }
    return checkObjectStructure(value, constraints)
}

const checkContactDetails = (value: unknown): boolean => {
    if (!isObject(value)) {
        return false
    }
    const constraints: StructureConstraints = {
        firstName: {types: ['string', 'undefined']},
        lastName: {types: ['string', 'undefined']},
    }
    return checkObjectStructure(value, constraints)
}

const checkActivityHistoryInfo = (value: unknown): boolean => {
    if (!isObject(value)) {
        return false
    }
    const constraints: StructureConstraints = {
        id: {types: ['string', 'null']},
        caseId: {types: ['string', 'undefined']},
        contactId: {types: ['string', 'undefined']},
        voiceCallId: {types: ['string', 'undefined']},
    }
    return checkObjectStructure(value, constraints)
}

const complexTypeCheckers: ComplexTypeCheckers = {
    'primitiveObject': checkPrimitiveNotNestingObject,
    'interactionAssociatedObject': checkInteractionAssociatedObject,
    'interactionAssociatedObjectsArray': checkInteractionAssociatedObjectsArray,
    'externalKBItems': checkExternalKBItems,
    'externalKBSearchResults': checkExternalKBSearchResults,
    'externalKBArticle': checkExternalKBArticle,
    'formattedDate': checkDateFormat,
    'phoneDeviceData': checkPhoneDeviceData,
    'dispositionTarget': checkDispositionTarget,
    'loginData': checkLoginData,
    'rescheduleData': checkRescheduleData,
    'arrayOfStrings': checkArrayOfStrings,
    'dialCandidates': checkDialCandidates,
    'phoneNumbers': checkPhoneNumbers,
    'recordsValidationResult': checkRecordsValidationResult,
    'contactDetails': checkContactDetails,
    'activityHistoryInfo': checkActivityHistoryInfo,
}

const checkTypeConstraint = (value: unknown, constraint: Constraint): boolean => {
    if (constraint.types?.length) {
        if (constraint.types.includes('null') && value === null) {
            return true
        }
        if (array(constraint.types).includes(typeof value)) {
            return true
        }
        const complex = complexTypes.filter(type => {
            assertDefined(constraint.types)
            return constraint.types.includes(type)
        })
        for (const c of complex) {
            if (complexTypeCheckers[c](value)) {
                return true
            }
        }
        return false
    }
    if (constraint.values?.length) {
        return constraint.values.includes(value)
    }
    return false
}

export function checkConstraintsAndThrowIfDosntValid(params: unknown[], constraints: Constraint[]): void {

    if (params.length > constraints.length) {
        throw new ApiError(
            'invalid_args',
            `Expected to receive ${constraints.length} parameters, but got ${params.length}`,
        )
    }
    constraints.forEach((constraint, i) => {
        if (!checkTypeConstraint(params[i], constraint)) {
            if (constraint.types?.length) {
                throw new ApiError(
                    'invalid_args',
                    `Allowed types: ${constraint.types.join(', ')}. But got: "${JSON.stringify(params[i])}"`,
                )
            }
            if (constraint.values?.length) {
                throw new ApiError(
                    'invalid_args',
                    `Allowed values: ${constraint.values.join(', ')}. But got: "${JSON.stringify(params[i])}"`,
                )
            }
            throw new ApiError(
                'unknown_error',
                `Type constraint for this method isn't working. Please contact the support.`,
            )
        }
    })
}
